import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
  Checkbox,
  Divider,
} from "@mui/material";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
import AppConfig from "appConfig";
import MapPicker from "react-google-map-picker";
import GoogleAddressField from "app/components/google_address";

function CampaignLocationsCard({ campaignRef, triggerRefresh, campaignLocations }) {
  const navigate = useNavigate();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [locationTitle, setLocationTitle] = useState(null);
  const [locationLat, setLocationLat] = useState(null);
  const [locationLng, setLocationLng] = useState(null);
  const [locationRad, setLocationRad] = useState(50);
  const [restrictSalesToLocation, setRestrictSalesToLocation] = useState(true);
  const [enforceFaceMatch, setEnforceFaceMatch] = useState(true);
  const [openLocationDialog, setOpenLocationDialog] = useState(false);
  const [selectedCampaignLocation, setSelectedCampaignLocation] = useState(null);
  const [checkinGracePeriod, setCheckinGracePeriod] = useState(null);
  const [openRequestCheckinDialog, setOpenRequestCheckinDialog] = useState(false);
  const [locationCheckInTimeOfDay, setLocationCheckInTimeOfDay] = useState(null);
  const [openScheduleCheckinDialog, setOpenScheduleCheckinDialog] = useState(false);
  const [centerLocation, setCenterLocation] = useState(null);

  async function updateCampaignLocationStatus(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/locations/${ref}/status.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function deleteCampaignLocation(ref) {
    setShowLoading(true);
    await axios
      .get(`/portal/campaigns/locations/${ref}/delete.json`)
      .then((result) => {
        // console.log(result);
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  const campaignLocationsTableColumns = [
    { Header: "Location Name", accessor: "title", align: "left" },
    { Header: "Coverage Radius", accessor: "radius", align: "left" },
    { Header: "Geo-Fence Restricted", accessor: "restricted", align: "center" },
    { Header: "Location Status", accessor: "status", align: "center" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildCampaignLocationsRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: row.title,
        radius: `${row.coverage_radius}m`,
        restricted: (
          <MDBox>
            <Icon color={row.restrict_sales ? "success" : "error"}>
              {row.restrict_sales ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        status: (
          <MDBox>
            <Icon color={row.status ? "success" : "error"}>
              {row.status ? "check_circle" : "cancel"}
            </Icon>
          </MDBox>
        ),
        actions: (
          <MDBox sx={{ width: "100%" }}>
            <MDBox mt={1} display="flex" justifyContent="space-evenly" alignItems="start">
              <Tooltip title="Request Agent Check-in">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setSelectedCampaignLocation(row.reference);
                    setOpenRequestCheckinDialog(true);
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    person_pin
                  </Icon>
                </MDButton>
              </Tooltip>
              &nbsp;
              <Tooltip title="Agent Check-ins">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    navigate(`/location-check-ins/${row.reference}`);
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    list
                  </Icon>
                </MDButton>
              </Tooltip>
              &nbsp;&nbsp;
              <Tooltip title="View On Map">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="warning"
                  iconOnly
                  onClick={() => {
                    const maplink = `https://maps.google.com?q=${row.latitude},${row.longitude}`;
                    window.open(maplink, "_blank");
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    location_on
                  </Icon>
                </MDButton>
              </Tooltip>
              &nbsp;
              <Tooltip title={`${row.status ? "Disable" : "Enable"} Location?`}>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="info"
                  iconOnly
                  onClick={() => {
                    if (
                      window.confirm(`${row.status ? "Disable" : "Enable"} this location?`) === true
                    ) {
                      updateCampaignLocationStatus(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    {row.status ? "pause" : "play_arrow"}
                  </Icon>
                </MDButton>
              </Tooltip>
              &nbsp;
              <Tooltip title="Delete Location?">
                <MDButton
                  size="small"
                  variant="outlined"
                  color="error"
                  iconOnly
                  onClick={() => {
                    if (window.confirm(`Delete location?`) === true) {
                      deleteCampaignLocation(row.reference);
                    }
                  }}
                >
                  <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                    delete
                  </Icon>
                </MDButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        ),
        created: row.created,
      });
      return null;
    });

    return rows;
  };

  function onLocationPicked(clat, clng) {
    setLocationLat(clat);
    setLocationLng(clng);
  }

  useEffect(() => {
    if (locationLat === null) {
      return;
    }
    if (locationLng === null) {
      return;
    }

    setCenterLocation({
      lat: locationLat,
      lng: locationLng,
    });
  }, [locationLat, locationLng]);

  useEffect(() => {
    const cLocs = buildCampaignLocationsRows(campaignLocations);
    setTableRows(cLocs);
  }, [campaignLocations]);

  async function createCampaignLocation() {
    if (locationTitle === null) {
      setSeverity("error");
      setErrMsg("A location name is required.");
      setOpen(true);
      return;
    }

    if (locationLat === null) {
      setSeverity("error");
      setErrMsg("Location latitude is required");
      setOpen(true);
      return;
    }

    if (locationLng === null) {
      setSeverity("error");
      setErrMsg("Location longitude is required");
      setOpen(true);
      return;
    }

    if (locationRad === null) {
      setSeverity("error");
      setErrMsg("Location area radius is required");
      setOpen(true);
      return;
    }

    if (parseInt(locationRad, 10) < 30) {
      setSeverity("error");
      setErrMsg("Location area radius is too restricted. A minimum of 30 meters is required.");
      setOpen(true);
      return;
    }

    setOpenLocationDialog(false);
    setShowLoading(true);
    const json = {
      campaign: campaignRef,
      title: locationTitle,
      latitude: locationLat,
      longitude: locationLng,
      radius: parseInt(locationRad, 10),
      restrict_sales: restrictSalesToLocation,
    };

    await axios
      .post("portal/campaigns/locations/create.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function requestAgentCheckin() {
    if (parseInt(checkinGracePeriod, 10) < 10) {
      setSeverity("error");
      setErrMsg("Grace period is too short. A minimum of 10 minutes is required.");
      setOpen(true);
      return;
    }

    setOpenRequestCheckinDialog(false);
    setShowLoading(true);
    const json = {
      reference: selectedCampaignLocation,
      grace_period: parseInt(checkinGracePeriod, 10),
      verification_required: enforceFaceMatch,
    };

    await axios
      .post(`portal/campaigns/locations/${selectedCampaignLocation}/request/checkin.json`, json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  async function scheduleAgentCheckin() {
    if (parseInt(checkinGracePeriod, 10) < 10) {
      setSeverity("error");
      setErrMsg("Grace period is too short. A minimum of 10 minutes is required.");
      setOpen(true);
      return;
    }

    if (parseInt(locationCheckInTimeOfDay, 10) < 10) {
      setSeverity("error");
      setErrMsg("Please select a time of day for check-ins.");
      setOpen(true);
      return;
    }

    setOpenScheduleCheckinDialog(false);
    setShowLoading(true);
    const json = {
      reference: selectedCampaignLocation,
      grace_period: parseInt(checkinGracePeriod, 10),
      time: parseInt(locationCheckInTimeOfDay, 10),
      verification_required: enforceFaceMatch,
    };

    await axios
      .post(
        `portal/campaigns/locations/${selectedCampaignLocation}/request/checkin/schedule.json`,
        json
      )
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openRequestCheckinDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Request Agent Check-In</MDBox>
          <MDBox mt={1}>
            <MDTypography variant="body1" fontSize="small">
              Prompt your agents to perform physical check-ins at this location
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={1}>
              <MDInput
                name="latitude"
                margin="dense"
                label="Grace Period (in minutes)"
                type="number"
                value={checkinGracePeriod}
                fullWidth
                variant="outlined"
                onChange={(e) => setCheckinGracePeriod(e.target.value)}
              />
            </MDBox>
            <MDBox mt={2} display="flex" alignItems="center" ml={-1}>
              <Checkbox
                checked={enforceFaceMatch}
                onChange={(e) => {
                  setEnforceFaceMatch(e.target.checked);
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Enforce Face Match? (Requires verified agent accounts)
              </MDTypography>
            </MDBox>
            <MDBox mt={3} align="right" mb={3}>
              <MDButton
                size="small"
                color="dark"
                startIcon={<Icon>timer</Icon>}
                onClick={() => {
                  setOpenRequestCheckinDialog(false);
                  setOpenScheduleCheckinDialog(true);
                }}
                variant="outlined"
              >
                Want to schedule Check-In? Click here.
              </MDButton>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenRequestCheckinDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              requestAgentCheckin();
            }}
          >
            Request Check-In
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openScheduleCheckinDialog} fullWidth maxWidth="xs">
        <DialogTitle>
          <MDBox>Schedule Agent Check-In</MDBox>
          <MDBox mt={1}>
            <MDTypography variant="body1" fontSize="small">
              Schedule when your agents should perform physical check-ins at this location.
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={2}>
              <FormControl sx={{ minWidth: 120, width: "100%" }}>
                <InputLabel>Time Of Day</InputLabel>
                <Select
                  label="Time Of Day"
                  onChange={(e) => {
                    setLocationCheckInTimeOfDay(e.target.value);
                  }}
                  sx={{ padding: "12px", paddingLeft: "3px" }}
                >
                  <MenuItem value="00">12 AM</MenuItem>
                  <MenuItem value="1">1 AM</MenuItem>
                  <MenuItem value="2">2 AM</MenuItem>
                  <MenuItem value="4">4 AM</MenuItem>
                  <MenuItem value="5">5 AM</MenuItem>
                  <MenuItem value="6">6 AM</MenuItem>
                  <MenuItem value="7">7 AM</MenuItem>
                  <MenuItem value="8">8 AM</MenuItem>
                  <MenuItem value="9">9 AM</MenuItem>
                  <MenuItem value="10">10 AM</MenuItem>
                  <MenuItem value="11">11 AM</MenuItem>
                  <MenuItem value="12">12 PM</MenuItem>
                  <MenuItem value="13">1 PM</MenuItem>
                  <MenuItem value="14">2 PM</MenuItem>
                  <MenuItem value="15">3 PM</MenuItem>
                  <MenuItem value="16">4 PM</MenuItem>
                  <MenuItem value="17">5 PM</MenuItem>
                  <MenuItem value="18">6 PM</MenuItem>
                  <MenuItem value="19">7 PM</MenuItem>
                  <MenuItem value="20">8 PM</MenuItem>
                  <MenuItem value="21">9 PM</MenuItem>
                  <MenuItem value="22">10 PM</MenuItem>
                  <MenuItem value="23">11 PM</MenuItem>
                </Select>
              </FormControl>
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                name="grace_period"
                margin="dense"
                label="Grace Period (in minutes)"
                type="number"
                value={checkinGracePeriod}
                fullWidth
                variant="outlined"
                onChange={(e) => setCheckinGracePeriod(e.target.value)}
              />
            </MDBox>
            <MDBox mt={2} display="flex" alignItems="center" ml={-1}>
              <Checkbox
                checked={enforceFaceMatch}
                onChange={(e) => {
                  setEnforceFaceMatch(e.target.checked);
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Enforce Face Match? (Requires verified agent accounts)
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenScheduleCheckinDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              scheduleAgentCheckin();
            }}
          >
            Schedule Check-In
          </MDButton>
        </DialogActions>
      </Dialog>
      <Dialog open={openLocationDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          <MDBox>Create Location</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={0}>
              <MDInput
                name="title"
                margin="dense"
                label="Location Name"
                type="text"
                fullWidth
                variant="outlined"
                onChange={(e) => setLocationTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={3}>
              <MDBox>
                <GoogleAddressField
                  displayName="Search Address On Google Maps"
                  setAddress={() => {}}
                  setLatitude={setLocationLat}
                  setLongitude={setLocationLng}
                />
              </MDBox>
            </MDBox>
            <MDBox mt={3}>
              <MDInput
                name="coverage_radius"
                margin="dense"
                label="Coverage Radius Around Location (in meters)"
                type="number"
                value={locationRad}
                fullWidth
                variant="outlined"
                onChange={(e) => setLocationRad(e.target.value)}
              />
            </MDBox>
            {/* <MDBox mt={3}>
              <Divider />
            </MDBox>
            <MDBox mt={3}>
              <MDTypography variant="h6" fontWeight="regular">
                Advanced Options
              </MDTypography>
            </MDBox> */}
            <MDBox mt={2} display="flex" alignItems="center" ml={-1}>
              <Checkbox
                disabled
                checked={restrictSalesToLocation}
                onChange={(e) => {
                  setRestrictSalesToLocation(e.target.checked);
                }}
                inputProps={{ "aria-label": "controlled" }}
              />
              <MDTypography
                variant="button"
                fontWeight="regular"
                color="text"
                sx={{ cursor: "pointer", userSelect: "none", ml: -1 }}
              >
                &nbsp;&nbsp;Geo-Fencing Enabled. (Restricts activity within location radius)
              </MDTypography>
            </MDBox>

            {centerLocation !== null && (
              <MDBox mt={5} mb={3}>
                <div style={{ height: "50vh", width: "100%" }}>
                  <MDBox>
                    <MapPicker
                      defaultLocation={centerLocation}
                      zoom={18}
                      mapTypeId="roadmap"
                      style={{ height: "50vh" }}
                      onChangeLocation={(lat, lng) => {
                        onLocationPicked(lat, lng);
                      }}
                      apiKey={AppConfig.google_map_key}
                    />
                  </MDBox>
                </div>
              </MDBox>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenLocationDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createCampaignLocation();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3}>
        <Card sx={{ height: "100%", marginTop: "0px" }}>
          <MDBox>
            <MDBox p={2}>
              <MDBox display="flex" justifyContent="space-between" alignItems="center">
                <MDTypography variant="h6" fontWeight="light">
                  <b>Locations (Geo-Fencing)</b>
                  <br />
                  <small>
                    Run campaigns at specific physical locations. Restrict activity to only agents
                    or customers within these locations.
                  </small>
                </MDTypography>
                <MDButton
                  variant="contained"
                  color="dark"
                  iconOnly
                  onClick={() => {
                    setLocationTitle(null);
                    setLocationLat(null);
                    setLocationLng(null);
                    setRestrictSalesToLocation(true);
                    setLocationRad(50);
                    setOpenLocationDialog(true);
                  }}
                >
                  <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                </MDButton>
              </MDBox>
            </MDBox>
            <MDBox>
              <DataTable
                table={{ columns: campaignLocationsTableColumns, rows: tableRows }}
                isSorted
                entriesPerPage={false}
                showTotalEntries
                buttons
                noEndBorder
              />
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

export default CampaignLocationsCard;
